import React, { createContext, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';

/**
 * Contexte de batching pour les Sparkline (DataLoader pattern).
 *
 * Au lieu que chaque composant Sparkline fasse son propre appel
 * GET /api/market/history/{symbol}, on accumule les demandes pendant
 * une courte fenêtre (debounce 80 ms) puis on fait UN seul appel
 * POST /api/market/history/batch.
 *
 * Pour 40 sparklines :
 *   - Avant : 40 requêtes HTTP séquentielles/concurrentes
 *   - Après : 1 requête HTTP (parallélisation côté serveur via asyncio.gather)
 *
 * Usage :
 *   const { requestHistory } = useSparklineBatch();
 *   useEffect(() => {
 *     requestHistory(symbol, assetType, period).then(setPoints);
 *   }, [symbol, assetType, period]);
 */

const SparklineBatchContext = createContext({
  requestHistory: () => Promise.resolve([]),
});

export const SparklineBatchProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  // queue[period] = [{key, symbol, asset_type, resolve}]
  const queueRef = useRef({});
  const timerRef = useRef({});

  const flushQueue = useCallback(async (period) => {
    timerRef.current[period] = null;
    const batch = queueRef.current[period] || [];
    queueRef.current[period] = [];
    if (batch.length === 0) return;

    // Dédoublonnage : même symbol+asset_type → une seule fois côté API,
    // mais on résout toutes les promesses en attente.
    const bySymbol = new Map();
    for (const entry of batch) {
      const k = `${entry.asset_type}|${entry.symbol.toUpperCase()}`;
      if (!bySymbol.has(k)) bySymbol.set(k, []);
      bySymbol.get(k).push(entry.resolve);
    }

    const items = Array.from(bySymbol.keys()).map((k) => {
      const [asset_type, symbol] = k.split('|');
      return { symbol, asset_type };
    });

    try {
      const { data } = await axios.post(
        `${API}/market/history/batch`,
        { items, period },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = data?.results || {};
      bySymbol.forEach((resolvers, k) => {
        const [, symbol] = k.split('|');
        const r = results[symbol.toUpperCase()] || results[symbol] || { points: [] };
        resolvers.forEach((res) => res(r.points || []));
      });
    } catch {
      bySymbol.forEach((resolvers) => {
        resolvers.forEach((res) => res([]));
      });
    }
  }, [token]);

  const requestHistory = useCallback((symbol, assetType = 'stock', period = '1mo') => {
    return new Promise((resolve) => {
      if (!queueRef.current[period]) queueRef.current[period] = [];
      queueRef.current[period].push({ symbol, asset_type: assetType, resolve });
      if (!timerRef.current[period]) {
        timerRef.current[period] = setTimeout(() => flushQueue(period), 80);
      }
    });
  }, [flushQueue]);

  return (
    <SparklineBatchContext.Provider value={{ requestHistory }}>
      {children}
    </SparklineBatchContext.Provider>
  );
};

export const useSparklineBatch = () => useContext(SparklineBatchContext);

export default SparklineBatchContext;
